<script setup lang="ts">
import { formatNumber, formatRelativeTime } from '@/utils/format'
import type { DashboardMetric, MediaProblemRow } from '../../stores/dashboard'

// The "Media processing" section of the staff dashboard (RF-132): seven tiles and the recent problems list.
// It only shows what it is given and emits where to go; the dashboard owns navigation.
defineProps<{
  tiles: DashboardMetric[]
  problems: MediaProblemRow[]
  /** The summary call failed: tiles already show dashes, the list says so. */
  failed: boolean
  periodLabel: string
}>()

const emit = defineEmits<{
  navigate: [target: { route: string; query: Record<string, string> }]
}>()
</script>

<template>
  <section data-testid="media-section">
    <div class="text-subtitle1 text-weight-medium q-mb-sm">
      Media processing ({{ periodLabel }})
    </div>
    <div class="row q-col-gutter-md q-mb-md">
      <div v-for="tile in tiles" :key="tile.label" class="col-6 col-sm-4 col-md-3">
        <q-card
          flat
          bordered
          class="cursor-pointer"
          data-testid="media-tile"
          tabindex="0"
          role="link"
          @click="tile.route && emit('navigate', { route: tile.route, query: tile.query ?? {} })"
          @keyup.enter="
            tile.route && emit('navigate', { route: tile.route, query: tile.query ?? {} })
          "
        >
          <q-card-section>
            <div class="text-caption text-grey-7">{{ tile.label }}</div>
            <div
              class="text-h5 text-weight-bold"
              :class="tile.color || 'text-secondary'"
              data-testid="media-tile-count"
            >
              {{ tile.count === null ? '—' : (tile.display ?? formatNumber(tile.count)) }}
            </div>
            <div class="text-caption text-grey-7" data-testid="media-tile-sub">
              {{ tile.sub ?? '&nbsp;' }}
            </div>
          </q-card-section>
        </q-card>
      </div>
    </div>

    <div class="text-subtitle1 text-weight-medium q-mb-sm">Recent problems</div>
    <q-card flat bordered class="bg-white q-mb-lg">
      <div v-if="failed" class="q-pa-md text-grey-7" data-testid="media-problems-error">
        Couldn't load media stats.
      </div>
      <div
        v-else-if="problems.length === 0"
        class="q-pa-md text-grey-7"
        data-testid="media-problems-empty"
      >
        No failed or threat files.
      </div>
      <q-list v-else separator role="group" aria-label="Recent problems">
        <q-item
          v-for="problem in problems"
          :key="problem.fileId"
          clickable
          data-testid="recent-problem"
          @click="emit('navigate', { route: problem.route, query: problem.query })"
        >
          <q-item-section avatar>
            <q-icon
              :name="problem.kind === 'threat' ? 'mdi-shield-bug' : 'mdi-alert'"
              :color="problem.kind === 'threat' ? 'negative' : 'orange-9'"
              :data-kind="problem.kind"
              data-testid="recent-problem-icon"
            />
          </q-item-section>
          <q-item-section>
            <q-item-label class="ellipsis">{{ problem.fileName }}</q-item-label>
          </q-item-section>
          <q-item-section side class="text-grey-8">
            {{ problem.stage ?? (problem.kind === 'threat' ? 'Virus scan' : '—') }}
          </q-item-section>
          <q-item-section side class="text-grey-7">
            {{ formatRelativeTime(problem.occurredAt) }}
          </q-item-section>
          <q-item-section side><q-icon name="mdi-chevron-right" /></q-item-section>
        </q-item>
      </q-list>
    </q-card>
  </section>
</template>
